import {
  confirmFeeApproveFlow,
  confirmFeeDeleteFlow,
  notifyFeeActionPending,
  openFeeEditModalFromRow,
  resolveFeeApprovalTargetState,
  submitFeePayFlow,
} from '@/composables/fees/useFeeFlow';
import {
  fetchFeesFilterOptions,
  fetchFeesSnapshot,
  getFeesFilterOptions,
} from '@/composables/fees/useFeesList';
import {
  createDefaultSaleFeesFilters,
  createInitialFeeSummary,
} from '@/composables/sales/saleDetail/state';
import { ensureValidPage } from '@/composables/shared/usePagination';
import { FEES_TEXT } from '@/texts/catalog/fees';

const FEES_FLOW_TEXT = FEES_TEXT.flow;

export const supplierDetailFeeMethods = {
  resetSupplierFeeRuntime() {
    this.feeRows = [];
    this.feeSummary = createInitialFeeSummary();
    this.feeCurrentPage = 1;
    this.feeActiveStatusTab = 'all';
    this.feeFilters = createDefaultSaleFeesFilters();
    this.feeSearchText = '';
    this.feeInFlightRequestKey = '';
  },
  ensureFeePageInRange() {
    this.feeCurrentPage = ensureValidPage(this.feeCurrentPage, this.feeTotalPages);
  },
  clearFeeSearchDebounce() {
    if (!this.feeSearchDebounceTimer) {
      return;
    }

    clearTimeout(this.feeSearchDebounceTimer);
    this.feeSearchDebounceTimer = null;
  },
  scheduleFeeSearchFetch() {
    this.clearFeeSearchDebounce();

    this.feeSearchDebounceTimer = setTimeout(() => {
      this.feeSearchDebounceTimer = null;
      this.fetchSupplierFees();
    }, this.feeSearchDebounceMs);
  },
  normalizeSupplierFeesFilters(filters = {}) {
    const source = (filters && typeof filters === 'object') ? filters : {};

    return {
      search: String(source.search || '').trim(),
      date_start: String(source.date_start || '').trim(),
      date_end: String(source.date_end || '').trim(),
      provider: Array.isArray(source.provider) ? source.provider : [],
      collaborator: Array.isArray(source.collaborator) ? source.collaborator : [],
      expense_class: Array.isArray(source.expense_class) ? source.expense_class : [],
      category: Array.isArray(source.category) ? source.category : [],
      customer: Array.isArray(source.customer) ? source.customer : [],
      tags: Array.isArray(source.tags) ? source.tags : [],
      has_xml: Array.isArray(source.has_xml) ? source.has_xml : [],
      currency: Array.isArray(source.currency) ? source.currency : [],
      area: Array.isArray(source.area) ? source.area : [],
    };
  },
  buildSupplierFeesParams() {
    const filters = this.normalizeSupplierFeesFilters(this.feeFilters);

    return {
      search: String(this.feeSearchText || filters.search || '').trim(),
      date_start: filters.date_start,
      date_end: filters.date_end,
      provider: [this.supplierId],
      provider_id: this.supplierId,
      supplier_id: this.supplierId,
      collaborator: filters.collaborator,
      expense_class: filters.expense_class,
      category: filters.category,
      customer: filters.customer,
      tags: filters.tags,
      has_xml: filters.has_xml,
      currency: filters.currency,
      area: filters.area,
    };
  },
  async ensureSupplierFeeFiltersLoaded() {
    if (this.feeFiltersLoaded) {
      return;
    }

    try {
      this.feeFilterOptions = await fetchFeesFilterOptions();
    } catch (error) {
      this.feeFilterOptions = getFeesFilterOptions();
    } finally {
      this.feeFiltersLoaded = true;
    }
  },
  async fetchSupplierFees() {
    if (this.supplierId <= 0) {
      this.resetSupplierFeeRuntime();
      return;
    }

    await this.ensureSupplierFeeFiltersLoaded();

    const params = this.buildSupplierFeesParams();
    const requestKey = JSON.stringify(params);

    if (this.feeLoading && this.feeInFlightRequestKey === requestKey) {
      return;
    }

    const requestToken = ++this.feeListRequestToken;
    this.feeInFlightRequestKey = requestKey;
    this.feeLoading = true;

    try {
      const snapshot = await fetchFeesSnapshot(params);

      if (requestToken === this.feeListRequestToken) {
        const rows = Array.isArray(snapshot?.rows) ? snapshot.rows : [];
        const payload = snapshot?.metrics && typeof snapshot.metrics === 'object' ? snapshot.metrics : {};

        this.feeRows = rows;
        this.feeSummary = {
          total: Number(payload?.total_expenses ?? payload?.total ?? 0) || 0,
          paid: Number(payload?.paid ?? payload?.total_paid ?? 0) || 0,
          pending: Number(payload?.pending_to_pay ?? payload?.pending ?? 0) || 0,
        };
        this.ensureFeePageInRange();
      }
    } catch (error) {
      if (requestToken === this.feeListRequestToken) {
        this.feeRows = [];
        this.feeSummary = createInitialFeeSummary();
      }
    } finally {
      if (requestToken === this.feeListRequestToken) {
        this.feeLoading = false;
        if (this.feeInFlightRequestKey === requestKey) {
          this.feeInFlightRequestKey = '';
        }
      }
    }
  },
  handleSupplierFeesSearchChange(payload) {
    const nextSearch = String(payload?.search || '').trim();

    this.feeSearchText = nextSearch;
    this.feeFilters = {
      ...this.normalizeSupplierFeesFilters(this.feeFilters),
      search: nextSearch,
    };
    this.feeCurrentPage = 1;
    this.scheduleFeeSearchFetch();
  },
  handleSupplierFeesFiltersChange(payload) {
    const nextFilters = this.normalizeSupplierFeesFilters(payload);

    this.feeFilters = nextFilters;
    this.feeSearchText = String(nextFilters.search || '').trim();
    this.feeCurrentPage = 1;
    this.fetchSupplierFees();
  },
  handleSupplierFeesStatusTabSelect(tabKey) {
    const normalized = String(tabKey || '').trim();
    if (normalized === '' || normalized === this.feeActiveStatusTab) {
      return;
    }

    this.feeActiveStatusTab = normalized;
    this.feeCurrentPage = 1;
  },
  handleSupplierFeesPageChange(nextPage) {
    this.feeCurrentPage = Number(nextPage || 1);
    this.ensureFeePageInRange();
  },
  handleSupplierPayFee(row) {
    if (this.isPayFeeSubmitting) {
      return;
    }

    this.payFeeTarget = row && typeof row === 'object' ? { ...row } : null;
    this.isPayFeeModalOpen = true;
  },
  handleSupplierEditFee(row) {
    openFeeEditModalFromRow(this, row, {
      mapInitialData: ({ detail, rowData }) => {
        const mergedPayload = {
          ...rowData,
          ...detail,
        };

        if (String(mergedPayload.status || '').trim() === '') {
          mergedPayload.status = String(rowData.status || '').trim();
        }

        if (Number(mergedPayload.payment_id || 0) <= 0) {
          mergedPayload.payment_id = Number(
            detail.payment_id
            || rowData.payment_id
            || rowData.payment?.id
            || 0
          ) || 0;
        }

        if (mergedPayload.is_approved === undefined || mergedPayload.is_approved === null) {
          mergedPayload.is_approved = rowData.is_approved;
        }

        return mergedPayload;
      },
    });
  },
  handleSupplierApproveFee(row, nextApproved = null) {
    this.approveFeeTarget = row && typeof row === 'object' ? { ...row } : null;
    this.approveFeeTargetState = resolveFeeApprovalTargetState(row, nextApproved);
    this.isApproveFeeModalOpen = true;
  },
  handleSupplierDeleteFee(row) {
    this.deleteFeeTarget = row && typeof row === 'object' ? { ...row } : null;
    this.isDeleteFeeModalOpen = true;
  },
  handleSupplierDownloadFeeXml(row) {
    notifyFeeActionPending(this, row, { actionLabel: FEES_FLOW_TEXT.pending.actions.downloadXml });
  },
  handleSupplierDownloadFeePdf(row) {
    notifyFeeActionPending(this, row, { actionLabel: FEES_FLOW_TEXT.pending.actions.downloadPdf });
  },
  handleSupplierDownloadFeeReceipt(row) {
    notifyFeeActionPending(this, row, { actionLabel: FEES_FLOW_TEXT.pending.actions.downloadReceipt });
  },
  closeSupplierApproveFeeModal() {
    if (this.isApprovingFee) {
      return;
    }

    this.isApproveFeeModalOpen = false;
    this.approveFeeTarget = null;
    this.approveFeeTargetState = true;
  },
  closeSupplierPayFeeModal() {
    if (this.isPayFeeSubmitting) {
      return;
    }

    this.isPayFeeModalOpen = false;
    this.isPayFeeSubmitting = false;
    this.payFeeTarget = null;
  },
  closeSupplierDeleteFeeModal() {
    if (this.isDeletingFee) {
      return;
    }

    this.isDeleteFeeModalOpen = false;
    this.deleteFeeTarget = null;
  },
  async submitSupplierPayFee(payload = {}) {
    await submitFeePayFlow(this, payload, {
      onSuccess: async () => {
        await this.fetchSupplierDetail();
      },
    });
  },
  async confirmSupplierApproveFee() {
    await confirmFeeApproveFlow(this, {
      onSuccess: async () => {
        await this.fetchSupplierDetail();
      },
    });
  },
  async confirmSupplierDeleteFee() {
    await confirmFeeDeleteFlow(this, {
      onSuccess: async () => {
        await this.fetchSupplierDetail();
      },
    });
  },
  handleSupplierNewFeeFromOperation() {
    this.openFeeModal('create', this.buildFeeCreateInitialData());
  },
};
